// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import {
  types as t,
  usePropsValidation,
} from '../validation/runtime-checks/index.js';
import { Text } from '../Text.js';
import Latex from '../Latex.js';
import { List } from '../List.js';
import Image from '../Image.js';
import DraftImage from '../DraftImage.js';
import BoxAndWhiskerPlot from '../BoxAndWhiskerPlot.js';
import { CartesianPlane } from '../CartesianPlane.js';
import { NumberLine } from '../NumberLine.js';
import { StemAndLeafPlot } from '../StemAndLeafPlot.js';
import TabularData from '../TabularData.js';
import { TwoColumnProof } from '../TwoColumnProof.js';
import { AlgebraicWorking } from '../AlgebraicWorking.js';
import { EngageNoteCollapsibleInternal } from './EngageNoteCollapsibleInternal.js';
import { EANStrategyDetail } from './EANStrategyPanel.js';
import { productColor } from '../designSystem.js';
import { SubHeading } from '../Heading.js';
import { LatexTextAtom } from '../LatexTextAtom.js';

export function EANDiscussSection(props: {|
  timeEstimate: number,
  children:
    | [Element<typeof EANDiscussDescription>, Element<typeof EANDiscussPanel>]
    | Element<typeof EANDiscussPanel>,
|}) {
  const { failedValidation } = usePropsValidation(props, EANDiscussSection);
  if (failedValidation) return null;
  return (
    <div style={{ color: productColor.shuttleGrey, paddingBottom: 40 }}>
      <div style={{ marginTop: 40 }}>
        <SubHeading>Discuss</SubHeading>
        <div style={{ color: productColor.shuttleGrey }}>
          {props.timeEstimate} mins
        </div>
      </div>
      <div style={{ marginTop: 16 }}>{props.children}</div>
    </div>
  );
}
EANDiscussSection.spec = {
  children: t.unionOfTypes(
    t.tuple([
      t.elementType(EANDiscussDescription),
      t.elementType(EANDiscussPanel),
    ]),
    t.elementType(EANDiscussPanel),
  ),
  timeEstimate: t.number,
};

export function EANDiscussDescription(props: {|
  children: ChildrenArray<
    string | Element<typeof Latex> | Element<typeof LatexTextAtom>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(props, EANDiscussDescription);
  if (failedValidation) return null;
  return <div style={{ marginBottom: 2 }}>{props.children}</div>;
}
EANDiscussDescription.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
    ),
  ),
};

export function EANDiscussPanel(props: {|
  children: ChildrenArray<
    | Element<typeof Text>
    | Element<typeof Latex>
    | Element<typeof LatexTextAtom>
    | Element<typeof List>
    | Element<typeof Image>
    | Element<typeof DraftImage>
    // probably only need those above this line
    | Element<typeof BoxAndWhiskerPlot>
    | Element<typeof CartesianPlane>
    | Element<typeof NumberLine>
    | Element<typeof StemAndLeafPlot>
    | Element<typeof TabularData>
    | Element<typeof TwoColumnProof>
    | Element<typeof AlgebraicWorking>
    | Element<typeof EANStrategyDetail>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(props, EANDiscussPanel);
  if (failedValidation) return null;
  const children = React.Children.toArray(props.children);
  return (
    <EngageNoteCollapsibleInternal title="Discussion guide">
      {children.map((child, i) => (
        <React.Fragment key={i}>
          {child}
          {i !== children.length - 1 && <div style={{ height: 16 }} />}
        </React.Fragment>
      ))}
    </EngageNoteCollapsibleInternal>
  );
}
EANDiscussPanel.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      // Primitives
      t.elementType(Text),
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
      t.elementType(List),
      t.elementType(Image),
      t.elementType(DraftImage),
      // probably only need those above this line
      t.elementType(BoxAndWhiskerPlot),
      t.elementType(CartesianPlane),
      t.elementType(NumberLine),
      t.elementType(StemAndLeafPlot),
      t.elementType(TabularData),
      t.elementType(TwoColumnProof),
      t.elementType(AlgebraicWorking),
      t.elementType(EANStrategyDetail),
    ),
  ),
};
